import React, { useEffect, useState } from "react";
import { IExpense } from "../../types/expense.type";
import { ExpenseType } from "../../types/transaction.type";
import useGetRequest from "../../hooks/useGetRequest";
import { api } from "../../api/api";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { useAuth } from "../../contexts/auth.context";
import { canEditExpenses } from "../../utils/roleUtils";

const { Title } = Typography;

const EXPENSE_TYPE_ENTRIES = Object.entries(ExpenseType) as [keyof typeof ExpenseType, string][];

export default function Expenses() {
  const { authState } = useAuth();
  const [expenses, setExpenses] = useState<IExpense[]>([]);
  const { data, loading, refresh } = useGetRequest(`/expense`);
  const { data: costReportData, loading: costReportLoading } = useGetRequest(
    `/settings/cost-report-expense-types`
  );
  const [excludedTypes, setExcludedTypes] = useState<string[]>([]);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      setExpenses(data);
    }
  }, [data]);

  useEffect(() => {
    if (costReportData?.costReportExpenseTypesExcluded) {
      setExcludedTypes(costReportData.costReportExpenseTypesExcluded);
    }
  }, [costReportData]);

  const handleSubmitForm = async (values: Partial<IExpense>) => {
    setConfirmLoading(true);

    try {
      await api.post("/expense", values);
      message.success("Расход успешно создан.");

      refresh();
      setIsModalOpen(false);
      setConfirmLoading(false);
      form.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating expense", error);
      message.error(error.response.data.error);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await api.delete(`/expense/${expenseId}`);
      message.success("Расход успешно удален.");
      refresh();
    } catch (error: any) {
      console.error("Error deleting expense", error);
      message.error(error.response.data.error);
    }
  };

  const handleIncludeInCostChange = async (expense: IExpense, checked: boolean) => {
    try {
      await api.put(`/expense/${expense._id}`, {
        name: expense.name,
        includeInCostCalculation: checked,
      });
      message.success(
        checked ? "Тип расхода учитывается в себестоимости" : "Тип расхода исключён из себестоимости"
      );
      refresh();
    } catch (error: any) {
      console.error("Error updating expense", error);
      message.error(error.response?.data?.error ?? "Ошибка при обновлении");
    }
  };

  const handleExpenseTypeIncludeChange = async (enumKey: string, include: boolean) => {
    const newExcluded = include
      ? excludedTypes.filter((k) => k !== enumKey)
      : [...excludedTypes, enumKey];
    try {
      await api.post("/settings/cost-report-expense-types", {
        costReportExpenseTypesExcluded: newExcluded,
      });
      setExcludedTypes(newExcluded);
      message.success(
        include ? "Тип расхода учитывается в себестоимости" : "Тип расхода исключён из себестоимости"
      );
    } catch (error: any) {
      console.error("Error updating cost report expense types", error);
      message.error(error.response?.data?.error ?? "Ошибка при обновлении");
    }
  };

  const columns = [
    { title: "Имя расхода", dataIndex: "name", key: "name" },
    { title: "Создал", dataIndex: "ownerName", key: "ownerName" },
    {
      title: "Учитывать в себестоимости",
      dataIndex: "includeInCostCalculation",
      key: "includeInCostCalculation",
      render: (include: boolean | undefined, expense: IExpense) => (
        <Switch
          checked={include !== false}
          onChange={(checked) => handleIncludeInCostChange(expense, checked)}
          disabled={!canEditExpenses(authState.user?.role)}
        />
      ),
    },
    {
      title: "Дата",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => {
        const date = new Date(createdAt);
        const formattedDate = date.toLocaleDateString("ru-RU");
        return <p>{formattedDate}</p>;
      },
    },
    {
      title: "Действия",
      key: "actions",
      render: (_: any, expense: IExpense) => (
        <Space>
          {canEditExpenses(authState.user?.role) && (
            <Popconfirm
              title="Вы уверены, что хотите удалить этот расход?"
              onConfirm={() => handleDeleteExpense(expense._id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button type="link" danger>
                Удалить
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={2}>Расходы</Title>
        {canEditExpenses(authState.user?.role) && (
          <Button
            type="primary"
            onClick={() => setIsModalOpen(true)}
            style={{ marginBottom: 16 }}
          >
            Добавить
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={expenses}
        rowKey={(record) => record._id}
        pagination={false}
        loading={loading}
      />

      <Title level={4} style={{ marginTop: 32, marginBottom: 16 }}>
        Типы расхода (справочник) — учёт в себестоимости
      </Title>
      <Table
        dataSource={EXPENSE_TYPE_ENTRIES.map(([key, label]) => ({ key, label }))}
        rowKey="key"
        pagination={false}
        loading={costReportLoading}
        columns={[
          { title: "Тип расхода", dataIndex: "label", key: "label" },
          {
            title: "Учитывать в себестоимости",
            key: "include",
            render: (_: unknown, row: { key: string; label: string }) => (
              <Switch
                checked={!excludedTypes.includes(row.key)}
                onChange={(checked) => handleExpenseTypeIncludeChange(row.key, checked)}
                disabled={!canEditExpenses(authState.user?.role)}
              />
            ),
          },
        ]}
      />

      <Modal
        title="Добавить расход"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Имя расхода"
            rules={[{ required: true, message: "Введите имя расхода" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
